import Branch from 'models/branch';
import branchApi from 'api/branch';
import cloneDeep from 'lodash/cloneDeep';
import Col from 'components/Col';
import DealerApi from 'api/dealer';
import events from 'lib/events';
import FooterEditButton from 'components/layout/footerEditButton';
import hopscotch from 'hopscotch';
import PageFooter from 'components/layout/pageFooter';
import React from 'react';
import Row from 'components/Row';
import SelectStore from './Stores/SelectStore';
import session from 'services/sessionService';
import StoreHours from './Stores/StoreHours';
import StoreInfo from './Stores/StoreInfo';
import StoreProducts from './Stores/StoreProducts';
import StoreTesters from './Stores/StoreTesters';
import ValidationGroup from 'helpers/validation';
import waterTesterApi from 'api/waterTester';
import {dealerAdminTourFormat, userLevels} from 'helpers/constants';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';

class Stores extends React.Component {
  static propTypes= {
    intl: intlShape.isRequired,
  };

  state = {
    activeStoreID: -1,
    branches: [],
    fieldValidation: {},
    store: null,
    waterTesters: [],
    waterTestersBackup: [],
    edit: false,
    dealer: {},
    hasReplyEmailDupe: false,
  };

  componentWillMount() {
    this.group = new ValidationGroup(this._save);
  }

  componentDidMount() {
    let apis = [
      branchApi.getFullBranches(),
      waterTesterApi.getWaterTesters(),
      DealerApi.getDealer(session.getUser().dealerId),
    ];

    Promise.all(apis)
    .then(([branches, testers, dealer]) => {
      let currentUser = session.getUser();

      branches = branches.filter(b => currentUser.isDealerAdmin || currentUser.isBranchMember(b));

      for (let i = 0; i < branches.length; ++i) {
        let b = branches[i];

        if (!b.bioGuard && !b.proGuard && !b.spaGuard) {
          b.bioGuard = true;
          b.spaGuard = true;
        }
        if (!b.brandingName && dealer.name) {
          b.brandingName = dealer.name;
        }
      }

      let activeStoreID = -1;
      let store = null;

      if (branches && branches.length === 1) {
        activeStoreID = branches[0].id;
        store = branches.find(o => o.id === branches[0].id).clone();
      }

      this.setState({
        activeStoreID,
        branches,
        dealer,
        store,
        waterTesters: testers,
        waterTestersBackup: testers.map(x => x.clone()),
      });
    })
    .catch(err => {
      console.error(err);
      events.publish('error.user', {message: 'Could not fetch data.'});
    });

    if (hopscotch.getState() === 'dealerAdmin:6') {
      hopscotch.startTour(dealerAdminTourFormat(this.props.intl.formatMessage), 6);
    }
    if (hopscotch.getState() === 'dealerAdmin:10') {
      hopscotch.startTour(dealerAdminTourFormat(this.props.intl.formatMessage), 10);
    }
  }

  componentWillUnmount() {
    if (hopscotch.isActive && window.location.hash !== '#settings/customer' && window.location.hash !== '#settings/dealer') {
      hopscotch.endTour();
    }
  };

  onCancelEdit = () => {
    this.setState({
      edit: false,
      store: this.state.branches.find(o => o.id === this.state.activeStoreID).clone(),
      waterTesters: this.state.waterTestersBackup.map(x => x.clone()),
    });
  };

  onEdit = () => {
    this.setState({edit: true});
  };

  onStoreChanged = (event) => {
    if (event.target.value) {
      this.setState({
        activeStoreID: parseInt(event.target.value),
        store: this.state.branches.find(o => o.id === parseInt(event.target.value)).clone(),
      });
    } else {
      this.setState({
        activeStoreID: -1,
        store: null,
      });
    }
  };

  _save = () => {
    events.publish('water-testers.save-partials', {});

    let b64data = this.storeInfo.getImage();

    let apis = [];

    let store = this.state.store;

    if (!store.brandingName) {
      store.brandingName = this.state.dealer.name;
    }

    apis.push(branchApi.saveBranch(store, b64data));
    apis.push(waterTesterApi.saveWaterTesters(this.state.waterTesters));

    Promise.all(apis).then(([store, testers]) => {
      let index = this.state.branches.findIndex(b => b.id === this.state.activeStoreID);

      if (index < 0) {
        console.error(`Could not find saved branch with ID ${store.id}. The active store ID is ${this.state.activeStoreID}`);
        throw new Error('Could not update branch');
      }
      let branches = Object.assign([], this.state.branches);

      branches[index] = store.clone();

      this.storeInfo.clear();

      this.setState({
        edit: false,
        dealerBackup: null,
        store: store.clone(),
        branches: branches,
        waterTesters: testers,
        waterTestersBackup: testers.map(x => x.clone()),
      });
    }).catch(err => {
      console.error(err);
      events.publish('error.user', {message: 'Error saving store: ' + err.message});
    });
  };

  changeOverride = (event) => {
    let store = cloneDeep(this.state.store);

    store.overrideBranding = !event.value;
    this.setState({store});
  };

  dataChange = (event) => {
    let store = cloneDeep(this.state.store);

    store[event.key] = event.value;
    if(event.key === 'brandingName' && store.useEmail.toString() === 'true') {
      this.validateReplyEmailDuplicate(store);
    }
    this.setState({store});
  };

  deleteWaterTester = (id, name) => {
    let waterTesters = this.state.waterTesters.map(t => {
      if (t.id === id && t.name === name) {
        t.deleted = true;
      }
      return t;
    });

    this.setState({waterTesters: waterTesters});
  };

  editWaterTesters = (event) => {
    this.setState({waterTesters: event.value});
  };

  hoursChange = (event) => {
    let [day, section] = event.key.split('.');
    let store = new Branch(this.state.store || {});

    store.hours[day][section] = event.value;
    this.setState({store});
  };

  storeSelect = () => {
    let storeList;

    if (this.state.branches) {
      storeList = this.state.branches.map(b => {
        return <option key={b.id} value={b.id}>{ b.name }</option>;
      });

      storeList.unshift(<option key='empty' value=''><FormattedMessage id="general.select.store" /></option>);
    }

    return <div className='form-group'>
      <label><FormattedMessage id="general.select.store" />:</label>
      <select value={this.state.activeStoreID} className='form-control contact-select' style={{width: 'auto'}} onChange={this.onStoreChanged}>
        {storeList}
      </select>
    </div>;
  };

  useEmailValidation = (event) => {
    let store = cloneDeep(this.state.store);

    store.useEmail = event.value;
    if(store.useEmail === 'false' || store.useEmail === false) {
      this.setState({store});
    }
    this.validateReplyEmailDuplicate(store);
  };

  validateReplyEmailDuplicate = (store) => {
    if(store.useEmail.toString() === 'true') {
      branchApi.validateReplyEmailDuplicate(store)
        .then(validEmail =>{
          if(!validEmail) {
            events.publish('error.user', {
              message: <span><FormattedMessage id="preferences.error.store. company " /></span>,
            });
          }
          let newStore = cloneDeep(this.state.store);

          newStore.useEmail = validEmail;
          this.setState({hasReplyEmailDupe: !validEmail,store: newStore});
          events.publish('radioHaschange');
        });
    }
  };

  render() {
    let footer;

    if (session.getUser().privileges.level >= userLevels.manager) {
      footer = <PageFooter>
        <div style={{float: 'right'}}>
          <FooterEditButton
            editMode={this.state.edit}
            onEdit={this.onEdit}
            onCancel={this.onCancelEdit}
            enabled={!!this.state.store}
            onSave={this.group.validate} />
        </div>
      </PageFooter>;
    }

    const {edit, waterTesters, activeStoreID, dealer} = this.state;

    let store = <SelectStore />;

    if (this.state.store) {
      store = <Row>
        <Col md={9}>
          <StoreInfo edit={edit} onChange={this.dataChange} store={this.state.store} group={this.group} changeOverride={this.changeOverride}
            ref={si => this.storeInfo = si} dealer={dealer} useEmailValidation={this.useEmailValidation} formatMessage={this.props.intl.formatMessage}/>
        </Col>
        <Col md={3}>
          <StoreProducts store={this.state.store} edit={edit} onChange={this.dataChange} dealer={dealer} />
        </Col>
        <Col md={9}>
          <StoreHours edit={edit} key={this.state.store.id} hoursChange={this.hoursChange} store={this.state.store} formatMessage={this.props.intl.formatMessage} />
        </Col>
        <Col md={3}>
          <StoreTesters edit={edit} store={this.state.store} waterTesters={waterTesters} activeStoreID={activeStoreID}
                        deleteTester={this.deleteWaterTester} editTester={this.editWaterTesters} />
        </Col>
      </Row>;
    }

    return <div id='page-stores'>
      <Col xs={12}>
        {this.storeSelect()}
      </Col>
      <div className='clearfix'/>
      {store}
      {footer}
    </div>;
  }
}

export default injectIntl(Stores);
